// Central role vocabulary — mirrors CheqMe.Server.Contracts/Authorization/Roles.cs. These are the role
// claim values the monolith context token now carries (replacing the legacy "Owner"/"Admin").
export enum Roles {
  Employee = "Employee",
  ShardAdmin = "ShardAdmin",
  // A whole company (all its shards). Sits between ShardAdmin and PlatformOwner.
  CompanyOwner = "CompanyOwner",
  PlatformOwner = "PlatformOwner",
  // Additive privilege (held alongside a baseline role): may reveal unmasked sensitive submission data.
  SensitiveDataAccessor = "SensitiveDataAccessor",
  // Additive privilege: may read the audit trail (separate from SensitiveDataAccessor — separation of duties).
  Auditor = "Auditor",
  // External-only payroll accountant ("Lohnbuchhalter"), company-scoped, invited by a company owner.
  Payroll = "Payroll",
  // Standalone baseline role (held INSTEAD of ShardAdmin/Employee, not alongside them): read-only access to the
  // live service screens of its own company + shard — the open-tables overview today, a kitchen screen later.
  Operator = "Operator",
  // Shift lead ("Schichtleitung"): grantable per company or per shard, but bounded by TIME rather than by
  // breadth — only the event that is live right now, and its shift plan only within that event's hours plus a
  // grace margin. Opens the ordinary management view with everything it holds no role for hidden.
  ShiftLead = "ShiftLead",
  // Tax adviser ("Steuerberater:in"): an external, company-scoped role for the firm's own adviser (#767). Opens
  // the fiscal record of every location — technical security system, cash-point closings, DSFinV-K exports —
  // and nothing else. Shares the management view rather than getting its own shell, because one person
  // routinely holds this AND Payroll and the two have to add up.
  Steuerberater = "Steuerberater"
}

/** Every value of {@link Roles}, built once — membership is asked per route, per navigation. */
const CENTRAL_ROLE_NAMES: ReadonlySet<string> = new Set<string>(Object.values(Roles));

/**
 * Whether `value` is a **central role name** ({@link Roles}) rather than a **legacy endpoint-permission id**
 * (`"Company.Employee.List"`, …).
 *
 * Two vocabularies share one array and are just strings. A route's `data.roles` is normally a
 * `PermissionSet.default` — legacy permission ids, AND-evaluated — but some routes name central roles there
 * instead (`/audit` names `Roles.Auditor`, the operator panel names `Roles.Operator`). A rule written for one
 * vocabulary and applied to the other is wrong silently, which is exactly how a `ShardAdmin` came to be
 * admitted to the audit log.
 *
 * An **unknown** string answers `false`, i.e. "a legacy permission id". That is the safe direction here and only
 * here: route data ships inside this build, so a string this build has never heard of cannot be a role a newer
 * backend added. (Contrast `contextViewFor`, which reasons over the roles a *token* carries and must treat an
 * unknown one as additive.)
 */
export function isCentralRole(value: string): boolean {
  return CENTRAL_ROLE_NAMES.has(value);
}
